import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import httpClient from '../../../httpClient';
import { useSnackbar } from '../../common_modules/SnackbarContext';

const MSTeamsOAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const clearOAuthFlag = () => {
      try {
        sessionStorage.removeItem('msteams_oauth_in_progress');
      } catch (err) {
        // ignore
      }
    };
    const handleOAuthCallback = async () => {
      try {
        console.log('[msteams] MSTeamsOAuthCallback component mounted, processing callback');
        // Extract OAuth parameters from URL
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        const state = searchParams.get('state');
        
        console.log('[msteams] Params extracted', { 
          hasCode: !!code, 
          hasState: !!state, 
          hasError: !!error,
          stateValue: state 
        });

        // Handle OAuth errors
        if (error) {
          const errorMsg = errorDescription || error;
          clearOAuthFlag();
          showSnackbar(`MS Teams authentication failed: ${errorMsg}`, 'error');
          navigate('/msteams/auth-result?auth_success=false&error=' + encodeURIComponent(errorMsg));
          return;
        }

        // Validate required parameters
        if (!code) {
          clearOAuthFlag();
          showSnackbar('No authorization code provided', 'error');
          navigate('/msteams/auth-result?auth_success=false&error=' + encodeURIComponent('No authorization code provided'));
          return;
        }

        if (!state) {
          clearOAuthFlag();
          showSnackbar('Missing state parameter', 'error');
          navigate('/msteams/auth-result?auth_success=false&error=' + encodeURIComponent('Missing state parameter'));
          return;
        }

        // Parse state (format: org_id:user_id)
        const stateParts = state.split(':');
        if (stateParts.length !== 2) {
          showSnackbar('Invalid state parameter', 'error');
          navigate('/msteams/auth-result?auth_success=false&error=' + encodeURIComponent('Invalid state parameter'));
          return;
        }

        const orgId = stateParts[0];
        const userId = stateParts[1];

        // Get auth token
        const token = localStorage.getItem('ids_token');
        if (!token) {
          showSnackbar('Please log in first', 'error');
          navigate('/login');
          return;
        }

        // Send code to backend for processing
        console.log('[msteams] Sending OAuth code to backend', { orgId, userId, hasCode: !!code });
        const response = await httpClient.post(
          '/ids/integrations/msteams/process_oauth_code',
          {
            code: code,
            org_id: orgId,
            user_id: userId,
            state: state
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('[msteams] Backend response received', { status: response.data?.status, msg: response.data?.msg });
        if (response.data && response.data.status) {
          clearOAuthFlag();
          showSnackbar('Successfully connected to MS Teams', 'success');
          navigate('/msteams/auth-result?auth_success=true');
        } else {
          const errorMsg = response.data?.msg || 'Failed to process authentication';
          clearOAuthFlag();
          showSnackbar(errorMsg, 'error');
          navigate('/msteams/auth-result?auth_success=false&error=' + encodeURIComponent(errorMsg));
        }
      } catch (error) {
        console.error('[msteams] Error processing OAuth callback:', error);
        const errorMsg = error.response?.data?.msg || 'An error occurred during authentication';
        console.log('[msteams] Error details', { 
          message: errorMsg, 
          status: error.response?.status,
          hasResponse: !!error.response 
        });
        clearOAuthFlag();
        showSnackbar(errorMsg, 'error');
        navigate('/msteams/auth-result?auth_success=false&error=' + encodeURIComponent(errorMsg));
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate, showSnackbar]);

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      flexDirection: 'column',
      gap: '16px'
    }}>
      <div>Processing MS Teams authentication...</div>
      <div style={{ fontSize: '14px', color: '#A8A8A8' }}>Please wait...</div>
    </div>
  );
};

export default MSTeamsOAuthCallback;
